import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useMagicPenStore } from '@src/stores';

import { ICanvasObject, IEditingStickyNotes } from '@src/types';

interface UseTextEditingProps {
  editingStickyNote: IEditingStickyNotes | null;
  setEditingStickyNote: (note: IEditingStickyNotes | null) => void;
  emitAddAnnotation: (objects: ICanvasObject[]) => Promise<void>;
  emitUpdateAnnotation: (objects: ICanvasObject[]) => Promise<void>;
}

const useTextEditing = ({
  editingStickyNote,
  setEditingStickyNote,
  emitAddAnnotation,
  emitUpdateAnnotation,
}: UseTextEditingProps) => {
  const [textareaValue, setTextareaValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { objects, updateStickyNoteContent, setSelectedTextRange, setIsTextColorChangeMode } = useMagicPenStore(
    useShallow((state) => ({
      objects: state.objects,
      updateStickyNoteContent: state.updateStickyNoteContent,
      setSelectedTextRange: state.setSelectedTextRange,
      setIsTextColorChangeMode: state.setIsTextColorChangeMode,
    }))
  );

  // Update textarea value when editing starts
  useEffect(() => {
    if (editingStickyNote) {
      setTextareaValue(editingStickyNote.content);
    } else {
      // Reset textarea value when not editing
      setTextareaValue('');
    }
  }, [editingStickyNote]);

  // Focus the textarea when editing starts - useLayoutEffect ensures the DOM is updated before we access the ref
  useLayoutEffect(() => {
    if (textareaRef.current && editingStickyNote) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [editingStickyNote]);

  // Update textarea color when sticky note text color changes
  useEffect(() => {
    if (!editingStickyNote || !textareaRef.current) {
      return;
    }

    const currentStickyNote = objects.find((obj) => obj.id === editingStickyNote.id);
    const currentTextColor = currentStickyNote?.textColor || editingStickyNote.textColor || '#000000';

    textareaRef.current.style.color = currentTextColor;
  }, [editingStickyNote, objects]);

  const handleTextEditComplete = useCallback(
    async (newContent: string) => {
      if (!editingStickyNote) {
        return;
      }

      const currentObjects = useMagicPenStore.getState().objects;
      const existingObject = currentObjects.find((obj) => obj.id === editingStickyNote.id);
      if (existingObject) {
        const updatedObject: ICanvasObject = {
          ...existingObject,
          content: newContent,
          hasSynced: true,
        };

        updateStickyNoteContent(editingStickyNote.id, newContent, {
          hasSynced: true,
        });

        if (!existingObject.hasSynced) {
          await emitAddAnnotation([updatedObject]);
        } else {
          await emitUpdateAnnotation([updatedObject]);
        }
      }

      setEditingStickyNote(null);
      setSelectedTextRange(null);
      setIsTextColorChangeMode(false);
    },
    [
      editingStickyNote,
      updateStickyNoteContent,
      setEditingStickyNote,
      setSelectedTextRange,
      setIsTextColorChangeMode,
      emitAddAnnotation,
      emitUpdateAnnotation,
    ]
  );

  const handleTextEditCancel = useCallback(() => {
    setEditingStickyNote(null);
    setSelectedTextRange(null);
    setIsTextColorChangeMode(false);
  }, [setEditingStickyNote, setSelectedTextRange, setIsTextColorChangeMode]);

  const handleTextSelection = useCallback(
    (start: number | null, end: number | null) => {
      if (start !== null && end !== null && start !== end) {
        setSelectedTextRange({ start, end });
        setIsTextColorChangeMode(true);
      } else {
        setSelectedTextRange(null);
        setIsTextColorChangeMode(false);
      }
    },
    [setSelectedTextRange, setIsTextColorChangeMode]
  );

  return {
    textareaRef,
    textareaValue,
    setTextareaValue,
    handleTextEditComplete,
    handleTextEditCancel,
    handleTextSelection,
  };
};

export default useTextEditing;
